/**
 * The front-of-house state vocabulary.
 *
 * One place that turns a table's raw `status` + `dining_stage` (backend
 * `models.DiningStage`) into how it should *read* on the floor: a colour rail, one
 * lowercase word, and a coarse tone. The walk-through review found the same state spelled
 * two or three ways per screen (a coloured pill AND the words AND a kitchen-state line);
 * every FOH surface — the table card, the floor plan, the "needs attention" board — should
 * resolve state through here so it says the same thing the same way everywhere.
 *
 * Tailwind classes rather than hex so the cards stay in the existing design system; the
 * `tone` is for callers that want to branch on category ("is this table mid-food?") without
 * matching on the specific word.
 */
import { duration } from '@verlo/shared/lib/duration';

// tone → what the colour means. `idle` = nothing happening, `active` = in service,
// `food` = a course is with the kitchen, `attention` = needs a person now.
export const TONE = Object.freeze({
  IDLE: 'idle',
  ACTIVE: 'active',
  FOOD: 'food',
  ATTENTION: 'attention',
});

// The words a resting card shows, each with its rail + text colour and tone.
const STATES = Object.freeze({
  // `reserved` gets its own hue (violet) rather than a lighter `seated` blue — the two used
  // to differ only by one Tailwind shade, which read as the same state at a glance on the
  // floor plan (#29 review).
  free:      { word: 'free',      rail: 'bg-hairline',          text: 'text-ink-soft',    tone: TONE.IDLE },
  reserved:  { word: 'reserved',  rail: 'bg-verlo-hover',       text: 'text-verlo-hover', tone: TONE.ACTIVE },
  seated:    { word: 'seated',    rail: 'bg-ink-faint',         text: 'text-ink-soft',    tone: TONE.ACTIVE },
  ordering:  { word: 'ordering',  rail: 'bg-ink-soft',          text: 'text-ink',         tone: TONE.ACTIVE },
  starters:  { word: 'starters',  rail: 'bg-signal-warn-line',  text: 'text-signal-warn', tone: TONE.FOOD },
  mains:     { word: 'mains',     rail: 'bg-signal-warn-line',  text: 'text-signal-warn', tone: TONE.FOOD },
  dessert:   { word: 'dessert',   rail: 'bg-signal-warn-line',  text: 'text-signal-warn', tone: TONE.FOOD },
  bill:      { word: 'bill',      rail: 'bg-signal-stop-line',  text: 'text-signal-stop', tone: TONE.ACTIVE },
  cleaning:  { word: 'cleaning',  rail: 'bg-hairline',          text: 'text-ink-soft',    tone: TONE.IDLE },
  // Tonight only, a manager's (lib/tonight.js): no party sits here until it's back in use.
  off:       { word: 'out of use', rail: 'bg-ink-faint',        text: 'text-ink-faint',   tone: TONE.IDLE },
});

// Every `dining_stage` the backend can send → one of the words above. The ten stages
// collapse to five: a card doesn't need "starters served" vs "awaiting starters", it needs
// "this table is on starters".
const STAGE_TO_STATE = Object.freeze({
  seated: 'seated',
  ordering: 'ordering',
  awaiting_starters: 'starters',
  starters_served: 'starters',
  awaiting_mains: 'mains',
  mains_served: 'mains',
  awaiting_desserts: 'dessert',
  desserts_served: 'dessert',
  awaiting_bill: 'bill',
  bill_paid: 'bill',
});

/** The resolved state for a table: `{ key, word, rail, text, tone }`. Never throws. */
export function resolveTableState(table) {
  const status = String(table?.status || 'available').toLowerCase();
  if (table?.out_of_use && status !== 'occupied') return { key: 'off', ...STATES.off };
  if (status === 'available') return { key: 'free', ...STATES.free };
  if (status === 'reserved') return { key: 'reserved', ...STATES.reserved };
  if (status === 'cleaning') return { key: 'cleaning', ...STATES.cleaning };
  // occupied (or anything unexpected) → read the dining stage
  const key = STAGE_TO_STATE[String(table?.dining_stage || 'seated').toLowerCase()] || 'seated';
  return { key, ...STATES[key] };
}

/**
 * How a table's turn-time should read, or `null` when it doesn't apply (not occupied, or
 * never opened). `over` true → past the turn time by `minutes`; `over` false → `minutes`
 * still to go. The card shows the amber pill only when `over`.
 */
export function turnStatus(table, now = Date.now()) {
  const status = String(table?.status || '').toLowerCase();
  if (status !== 'occupied' || !table?.opened_at) return null;
  const opened = new Date(table.opened_at).getTime();
  if (Number.isNaN(opened)) return null;
  const limitMin = Number(table.time_limit_minutes) || 90;
  // `Math.floor`, not `round`: `Math.round(-0.4)` is `-0`, and `-0 >= 0` is true in JS — a
  // table would read "over" a few seconds before its actual turn time. Flooring the elapsed
  // minutes (as the list view already did) never crosses zero early.
  const diffMin = Math.floor((now - (opened + limitMin * 60_000)) / 60_000);
  return diffMin >= 0
    ? { over: true, minutes: diffMin, label: `${duration(diffMin)} over` }
    : { over: false, minutes: -diffMin, label: `${duration(-diffMin)} left` };
}

/** A word or phrase as it's shown on the floor: its first letter capital ("Mains",
 *  "Joined tonight", "Out of use"), so a card's parts all read alike beside "Birthday"
 *  (Martin, 27 Oct). The words themselves stay lower case for use mid-sentence. */
export const sentence = (s) => (typeof s === 'string' && s ? s[0].toUpperCase() + s.slice(1) : s);

/** The truthy parts, in order — for a "a · b · c" subline that skips missing bits. */
export function metaParts(...parts) {
  return parts.filter((p) => p !== null && p !== undefined && p !== '');
}
